<template>
  <v-dialog persistent max-width="800px" v-model="dialog" fullscreen transition="dialog-bottom-transition">
    <div class="course-edit-wrapper">
      <v-app-bar app color="white" flat class="app-bar-border">
        <v-btn icon @click="closeDialog(true)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-toolbar-title class="font-weight-bold">
          {{ isEditing ? "Kursplanung bearbeiten" : "Neue Kursplanung" }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn text color="primary" @click="saveCourseTimeline">
          <v-icon left>mdi-check</v-icon>
          Speichern
        </v-btn>
      </v-app-bar>

      <v-container class="form-container">
        <v-row v-if="courseTimelineCreate">
          <v-col cols="12">
            <v-card class="form-card" outlined>
              <v-card-title>Grundlagen</v-card-title>
              <v-card-subtitle>Wähle das Kursformat und ergänze die Basis-Infos.</v-card-subtitle>
              <v-card-text>
                <course-search
                  :value="courseTimelineCreate.course"
                  label="Kursformat"
                  rights="gf_c_plan"
                  @update="updateCourse"
                  outlined></course-search>
                <v-select
                  v-model="courseTimelineCreate.label"
                  :items="labels"
                  item-text="text"
                  item-value="_id"
                  label="Label (optional)"
                  outlined
                  class="mt-4"
                  clearable></v-select>
                <v-text-field
                  v-model="courseTimelineCreate.additionalInfo"
                  outlined
                  label="Zusatzinfo (optional)"
                  placeholder="z.B. Mindest-Teilnehmerzahl"></v-text-field>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12">
            <v-card class="form-card" outlined>
              <v-card-title>Zeitplanung</v-card-title>
              <v-card-subtitle>Lege fest, wann und wie oft der Kurs stattfindet.</v-card-subtitle>
              <v-card-text>
                <v-btn-toggle v-model="courseTimelineCreate.repeating" mandatory dense class="mb-6">
                  <v-btn :value="true">Wöchentlich </v-btn>
                  <v-btn :value="false">Begrenzt</v-btn>
                </v-btn-toggle>

                <v-select
                  v-if="courseTimelineCreate.repeating"
                  v-model="courseTimelineCreate.day"
                  :items="days"
                  item-text="text"
                  item-value="value"
                  outlined
                  label="Wochentag"></v-select>

                <div v-else>
                  <p class="body-2 grey--text text--darken-1">
                    Wähle den Zeitraum, in dem der Kurs stattfinden soll. Der Wochentag wird automatisch vom Startdatum übernommen.
                  </p>
                  <v-select
                    v-model="courseTimelineCreate.day"
                    :items="days"
                    item-text="text"
                    item-value="value"
                    outlined
                    label="Wochentag"></v-select>
                  <v-menu v-model="dateMenuVisible" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="visibleAfterText"
                        label="Anzeigen ab"
                        prepend-inner-icon="mdi-calendar-start"
                        readonly
                        outlined
                        v-bind="attrs"
                        v-on="on"></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="courseTimelineCreate.visibleAfter"
                      :allowed-dates="allowedDates(selectedDay)"
                      @input="dateMenuVisible = false"
                      :min="minDate"
                      locale="de-CH"></v-date-picker>
                  </v-menu>

                  <v-menu v-model="dateMenuHide" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="hideAfterText"
                        label="Anzeigen bis"
                        prepend-inner-icon="mdi-calendar-end"
                        readonly
                        outlined
                        v-bind="attrs"
                        v-on="on"></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="courseTimelineCreate.hideAfter"
                      :allowed-dates="allowedDates(selectedDay)"
                      @input="dateMenuHide = false"
                      :min="courseTimelineCreate.visibleAfter"
                      locale="de-CH"></v-date-picker>
                  </v-menu>
                </div>

                <v-row>
                  <v-col cols="6">
                    <v-text-field v-model="time" type="time" outlined label="Uhrzeit"></v-text-field>
                  </v-col>
                  <v-col cols="6">
                    <v-text-field v-model="courseTimelineCreate.duration" type="number" outlined label="Dauer (Min)"></v-text-field>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12">
            <v-card class="form-card" outlined>
              <v-card-title>Kursleiter & Ort</v-card-title>
              <v-card-text>
                <div v-for="(instructor, index) in courseTimelineCreate.instructors" :key="index" class="d-flex align-center">
                  <user-search
                    instructor
                    :value="instructor"
                    :key="index + instructor + '-inst'"
                    label="Instruktor"
                    rights="gf_c_plan"
                    @update="(newVal) => updateInstructor(newVal, instructor)"
                    outlined
                    class="flex-grow-1"></user-search>
                  <v-btn icon color="grey" @click="removeInstructor(instructor)" class="ml-2">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </div>
                <v-btn text color="primary" @click="addInstructor" class="mt-2">
                  <v-icon left>mdi-plus</v-icon>
                  Instruktor hinzufügen
                </v-btn>
                <v-select
                  v-model="courseTimelineCreate.room"
                  :items="rooms"
                  item-text="name"
                  item-value="_id"
                  outlined
                  label="Raum"
                  class="mt-6"></v-select>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12">
            <v-card class="form-card" outlined>
              <v-card-title>Teilnahme & Darstellung</v-card-title>
              <v-card-text>
                <v-switch v-model="courseTimelineCreate.reqSignup" label="Anmeldung erforderlich"></v-switch>
                <v-text-field
                  v-if="courseTimelineCreate.reqSignup"
                  v-model="courseTimelineCreate.amount"
                  type="number"
                  outlined
                  label="Anzahl Plätze"></v-text-field>
                <v-divider class="my-4"></v-divider>
                <v-subheader>Kurs-Icon</v-subheader>
                <v-chip-group column v-model="courseTimelineCreate.icon" active-class="primary">
                  <v-chip large v-for="ic in icons" :key="ic" :value="ic" filter>
                    <v-avatar class="iconAvatar" center>
                      <lottie-player mode="normal" :src="'/api/gf/icons/' + ic"></lottie-player>
                    </v-avatar>
                  </v-chip>
                </v-chip-group>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </div>
  </v-dialog>
</template>

<script>
import api from "../../api"
import { mapActions } from "vuex"
import userSearch from "../../components/userSearch.vue"
import courseSearch from "../../components/courseSearch.vue"
export default {
  name: "courseCreateTimeline",
  components: { userSearch, courseSearch },
  data() {
    return {
      time: "00:00",
      dialog: true,
      minDate: null,
      dateMenuVisible: false, // NEU
      dateMenuHide: false, // NEU
      courseTimelineCreate: {
        amount: 0,
        room: null,
        duration: 0,
        instructors: [],
        day: 1,
        reqSignup: true,
        sendPush: false,
        course: null,
        repeating: true,
        visibleAfter: null,
        hideAfter: null,
        icon: null,
        lsLink: "",
        additionalInfo: "", // Hinzugefügt, falls es fehlte
        label: null, // Hinzugefügt, falls es fehlte
      },
      labels: [],
      icons: [],
      rooms: [],
      days: [
        { value: 1, text: "Montag" },
        { value: 2, text: "Dienstag" },
        { value: 3, text: "Mittwoch" },
        { value: 4, text: "Donnerstag" },
        { value: 5, text: "Freitag" },
        { value: 6, text: "Samstag" },
        { value: 7, text: "Sonntag" },
      ],
    }
  },
  destroyed() {
    document.body.style = "overflow:auto;max-height:100vh;"
  },
  async created() {
    document.body.style = "overflow:hidden;max-height:100vh;"
    this.minDate = this.$moment().format("YYYY-MM-DD")

    // Lade alle notwendigen Daten
    const [labels, rooms, icons] = await Promise.all([api.fetchGFLabels(), api.getRooms(), api.fetchAllGFIcons()])
    this.labels = labels
    this.rooms = rooms.room
    this.icons = icons.icons

    // Wenn eine ID in der Route ist, laden wir die existierenden Daten
    if (this.$route.params.id) {
      this.fetchCourseTimelineEdit()
    } else {
      // Standardwerte für einen neuen Kurs
      this.courseTimelineCreate.hideAfter = this.$moment().format("YYYY-MM-DD")
      this.courseTimelineCreate.visibleAfter = this.$moment().format("YYYY-MM-DD")
    }
  },
  methods: {
    ...mapActions("navigationStoreNotPersisted", ["setNavigationBarText"]),
    allowedDates(a) {
      return (val) => !a.includes(new Date(val).getDay())
    },
    async saveCourseTimeline() {
      if (this.courseTimelineCreate.instructors.includes(null)) {
        this.$toast.error("Bitte wähle für alle Felder einen Instruktor aus oder entferne die leeren Felder.")
        return // <-- Hier wird abgebrochen
      }
      if (!this.courseTimelineCreate.course) {
        return this.$toast.error("Bitte wähle ein Kursformat aus")
      }
      if (this.time === "00:00") {
        return this.$toast.error("Bitte gib eine gültige Uhrzeit an")
      }
      if (!this.courseTimelineCreate.repeating && (!this.courseTimelineCreate.visibleAfter || !this.courseTimelineCreate.hideAfter)) {
        return this.$toast.error("Bitte wähle ein Start- und Enddatum aus")
      }

      let formData = JSON.parse(JSON.stringify(this.courseTimelineCreate))
      formData.course = formData.course._id

      try {
        if (this.isEditing) {
          await api.updateCourseTimeline(formData._id, formData)
          this.$toast.success("Kursplanung erfolgreich aktualisiert!")
        } else {
          await api.createCourseTimeline(formData)
          this.$toast.success("Kursplanung erfolgreich erstellt!")
        }
        this.$emit("updateList")
        this.closeDialog(true)
      } catch (e) {
        this.$toast.error("Speichern fehlgeschlagen.")
      }
    },

    updateCourse(newCourse) {
      this.courseTimelineCreate.course = newCourse
    },

    removeInstructor(instructorId) {
      this.courseTimelineCreate.instructors = this.courseTimelineCreate.instructors.filter((id) => id !== instructorId)
    },

    addInstructor() {
      if (!this.courseTimelineCreate.instructors.includes(null)) {
        this.courseTimelineCreate.instructors.push(null)
      }
    },

    updateInstructor(newId, oldId) {
      if (oldId === null) {
        if (!this.courseTimelineCreate.instructors.includes(newId)) {
          this.courseTimelineCreate.instructors.pop() // Entfernt das 'null'
          this.courseTimelineCreate.instructors.push(newId) // Fügt die neue ID hinzu
        } else {
          this.courseTimelineCreate.instructors.pop() // Duplikat, 'null' entfernen
        }
      } else {
        const index = this.courseTimelineCreate.instructors.indexOf(oldId)
        if (index !== -1) {
          this.courseTimelineCreate.instructors.splice(index, 1, newId)
        }
      }
    },

    async fetchCourseTimelineEdit() {
      try {
        let courseTimeline = await api.fetchCourseTimeline(this.$route.params.id)
        this.courseTimelineCreate = courseTimeline.timeline

        let hours = String(this.courseTimelineCreate.hours).padStart(2, "0")
        let minutes = String(this.courseTimelineCreate.minutes).padStart(2, "0")
        this.time = `${hours}:${minutes}`
      } catch (e) {
        this.$toast.error("Kursplanung konnte nicht geladen werden.")
        this.closeDialog()
      }
    },

    closeDialog(resetTab = false) {
      this.setNavigationBarText(null)
      if (resetTab) {
        let tab = this.courseTimelineCreate.repeating ? 0 : 1
        this.$emit("resetTab", tab)
      }
      this.dialog = false
      this.$nextTick(() => {
        this.$router.push("/kurslisten")
      })
    },
  },
  watch: {
    time(val) {
      if (val) {
        this.courseTimelineCreate.hours = parseInt(val.split(":")[0])
        this.courseTimelineCreate.minutes = parseInt(val.split(":")[1])
      }
    },
    "courseTimelineCreate.visibleAfter"(val) {
      if (!this.courseTimelineCreate.repeating && val) {
        // Setzt den Wochentag basierend auf dem Startdatum
        const dayOfWeek = this.$moment(val).isoWeekday() // 1=Mo, 7=So
        this.courseTimelineCreate.day = dayOfWeek
      }
    },
  },
  computed: {
    selectedDay() {
      let day = this.courseTimelineCreate.day
      let days = [0, 1, 2, 3, 4, 5, 6]
      if (day === 7) day = 0
      days = days.filter((e) => e !== day)
      return days
    },
    isEditing() {
      return !!this.$route.params.id
    },
    visibleAfterText() {
      return this.courseTimelineCreate.visibleAfter ? this.$moment(this.courseTimelineCreate.visibleAfter).format("DD.MM.YYYY") : "Datum wählen"
    },
    hideAfterText() {
      return this.courseTimelineCreate.hideAfter ? this.$moment(this.courseTimelineCreate.hideAfter).format("DD.MM.YYYY") : "Datum wählen"
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep .iconAvatar {
  width: 50px !important;
  height: 50px !important;
}
.course-edit-wrapper {
  background-color: #f7f8fa; // Heller Hintergrund für die ganze Seite
  height: 100vh;
  width: 100vw;
  overflow-y: auto;
}

.app-bar-border {
  border-bottom: 1px solid #e0e0e0 !important;
}

.form-container {
  padding-top: 72px; // Platz für die App-Bar
  padding-bottom: 24px;
  max-width: 700px;
}

.form-card {
  border-radius: 16px;
  margin-bottom: 24px;
  border: 1px solid #e0e0e0;
}

.v-btn-toggle {
  width: 100%;
  .v-btn {
    width: 50%;
  }
}

// Lottie Player im Chip kleiner machen
::v-deep .v-chip .v-avatar .lottie-player {
  width: 32px !important;
  height: 32px !important;
}
</style>
